import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { liftOnHover, slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { motion } from "framer-motion";
import { useState } from "react";

const testimonials = [
  {
    id: 1,
    name: "Karen C",
    role: "Homeowner",
    houseImage: "https://rnrnbdqtileefwodurwk.supabase.co/storage/v1/render/image/public/images/properties/ce0eb4d8-d02b-418c-9e27-f2a65220e03d/cb623af6-4ceb-4800-befe-506ea53854a7.jpg?width=800&height=600&resize=cover&quality=40",
    content: "Excellent communication, professional and organized service. The website is easy to navigate with all the info you need to sell your home. Highly recommend and will definitely use again!",
    rating: 5,
    saved: "$23,000",
    property: "3-bedroom home in Keswick",
    avatar: "https://images.unsplash.com/photo-1575936123452-d666b0137e9e?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
  },
];

const Reviews = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const currentTestimonial = testimonials[activeIndex];

  const nextSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section>
      <motion.div
        className="text-center mb-12"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        <motion.span className="landing-badge" variants={staggerItem}>Reviews</motion.span>
        <motion.h2 className="landing-title" variants={staggerItem}>
          What Our Customers Say
        </motion.h2>
      </motion.div>

      <div className="relative">
        <motion.div
          variants={slideInFromBottom}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          <motion.div
            variants={liftOnHover}
            initial="rest"
            whileHover="hover"
          >
            <Card className="overflow-hidden shadow-lg border-0">
              <CardHeader>
                <CardTitle>{currentTestimonial.name}</CardTitle>
                <CardDescription>{currentTestimonial.property}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-2/5 h-full relative">
                    <div className="h-64 md:h-full w-full">
                      <img
                        src={currentTestimonial.houseImage}
                        alt="Customer's house"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex mb-4">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <motion.svg
                            key={i}
                            className={`h-5 w-5 ${i < currentTestimonial.rating ? "text-yellow-400" : "text-gray-300"}`}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: i * 0.1 }}
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </motion.svg>
                        ))}
                      </div>

                      <p className="text-gray-700 text-lg italic mb-6">"{currentTestimonial.content}"</p>
                    </div>

                    <div className="flex justify-between items-end">
                      <div className="flex gap-4">
                        <Avatar>
                          <AvatarImage src={currentTestimonial.avatar} />
                          <AvatarFallback>CN</AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="text-lg font-semibold text-black">{currentTestimonial.name}</h3>
                          <p className="text-sm text-gray-500">{currentTestimonial.role}</p>
                        </div>
                      </div>

                      <motion.div
                        className="bg-brand/10 px-5 py-3 rounded-lg text-center"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <p className="text-sm font-medium mb-1 text-gray-700">Commission Saved</p>
                        <p className="text-2xl font-bold text-brand">{currentTestimonial.saved}</p>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {testimonials.length > 1 && (
          <>
            <motion.button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all z-10"
              aria-label="Previous review"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
            <motion.button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all z-10"
              aria-label="Next review"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>

            <div className="flex justify-center mt-6 gap-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2.5 w-2.5 rounded-full transition-all ${index === activeIndex ? "bg-primary w-6" : "bg-gray-300"
                    }`}
                  aria-label={`Go to review ${index + 1}`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Reviews;